import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLocale } from "@/i18n/useLocale";
import { ROUTES } from "@/i18n/config";
import "./SectionNav.css";

/**
 * Puntos de navegacion por seccion.
 *
 * El orden refleja el de la home. Los ids son los reales de cada seccion:
 * antes "novedades" apuntaba a e-commerce y "sobre-nosotros" a software
 * factory, lo que hacia que los puntos activaran la seccion equivocada.
 */
const SECTION_IDS = [
  "home",
  "casos",
  "empleados",
  "ia",
  "ecommerce",
  "software-factory",
  "contacto",
] as const;

type SectionId = (typeof SECTION_IDS)[number];

function SectionNav() {
  const location = useLocation();
  const { t, locale } = useLocale();
  const [activeId, setActiveId] = useState<string>("home");

  const isHome =
    location.pathname === ROUTES.home[locale] ||
    location.pathname === "/" ||
    location.pathname === "/en";

  // Las etiquetas salen del diccionario: los puntos son un control de
  // navegacion y su nombre accesible tiene que estar en el idioma de la pagina.
  const labels: Record<SectionId, string> = {
    home: t.sectionNav.home,
    empleados: t.sectionNav.team,
    casos: t.sectionNav.cases,
    ia: t.sectionNav.ai,
    ecommerce: t.sectionNav.ecommerce,
    "software-factory": t.sectionNav.software,
    contacto: t.sectionNav.contact,
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        // Activa es la seccion que cruza el centro de la pantalla (una linea de
        // 0px al medio del viewport), sin importar su alto. Antes el umbral era
        // 0.5 -la mitad de la seccion visible-, y una seccion mas alta que dos
        // pantallas, como #empleados, nunca llegaba a activarse: el menu
        // seguia marcando la anterior mientras se la recorria.
        rootMargin: "-50% 0px -50% 0px",
        threshold: 0,
      }
    );

    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [isHome]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (!isHome) return null;

  return (
    <nav className="section-nav" aria-label={t.sectionNav.label}>
      {SECTION_IDS.map((id) => (
        <a
          key={id}
          href={`#${id}`}
          className={`section-nav__dot ${
            activeId === id ? "section-nav__dot--active" : ""
          }`}
          onClick={(e) => handleClick(e, id)}
          aria-label={labels[id]}
          title={labels[id]}
        />
      ))}
    </nav>
  );
}

export default SectionNav;
